import { X, Download, Pen } from 'lucide-react';
import { formatInvoiceDate } from '../../utils/invoiceHelpers';

/**
 * Invoice Preview / Print modal. Pure presentation — receives the invoice
 * + company info as props and renders the printable layout. The `printRef`
 * is forwarded to the inner div so the parent can hand it to PDF generators.
 *
 * Extracted from Invoices.js as part of the giant-component refactor
 * (Feb 2026). Behavior is byte-for-byte equivalent.
 */
const InvoicePreviewModal = ({
  open,
  previewInvoice,
  companyInfo,
  companySignature,
  printRef,
  onClose,
  onDownload,
  onRequestCustomerSignature,
}) => {
  if (!open || !previewInvoice) return null;
  // Optional doc-type override so this same modal can render both invoices
  // and estimates. Callers pass `_docType: 'estimate'` on the previewInvoice
  // (or fall back to the invoice defaults for backwards compatibility).
  const docType = previewInvoice._docType === 'estimate' ? 'estimate' : 'invoice';
  const docLabel = docType === 'estimate' ? 'Estimate' : 'Invoice';
  const docNumber = previewInvoice.estimateNumber || previewInvoice.invoiceNumber;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Sits above any host modal that launched it (e.g. the customer
        // history modal opens at z-index 1200).
        zIndex: 1300,
        padding: '2rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '10px',
          width: '100%',
          maxWidth: '820px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{docLabel} Preview</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => onDownload(previewInvoice)}
              data-testid="preview-download-pdf"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <Download size={16} />
              Download PDF
            </button>
            <button
              onClick={onClose}
              data-testid="close-preview-modal"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div ref={printRef} style={{ padding: 0, background: '#fff', position: 'relative' }}>
          {/* Top accent bar — pulled from Paradise-style invoice reference */}
          <div style={{ height: 8, background: '#249cdb' }} />

          {/* Status stamp — appears diagonally across the invoice body */}
          {(() => {
            // Estimates use their own status vocabulary (draft/sent/accepted/
            // rejected/converted) and never carry payment info, so we skip
            // the PAID PNG logic entirely for them and fall through to the
            // simple text stamp below.
            if (docType === 'estimate') {
              const map = {
                draft:     ['DRAFT',     'rgb(203, 213, 225)'],
                sent:      ['SENT',      'rgb(147, 197, 253)'],
                accepted:  ['ACCEPTED',  'rgb(134, 239, 172)'],
                rejected:  ['REJECTED',  'rgb(252, 165, 165)'],
                converted: ['CONVERTED', 'rgb(196, 181, 253)'],
              };
              const [label, light] = map[previewInvoice.status] || map.draft;
              return (
                <div
                  data-testid={`estimate-preview-stamp-${label.toLowerCase()}`}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '68%',
                    left: '62%',
                    transform: 'translate(-50%, -50%) rotate(-18deg)',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '4rem',
                    letterSpacing: '0.12em',
                    color: light,
                    border: `6px double ${light}`,
                    padding: '0.35rem 1.4rem',
                    borderRadius: 8,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    zIndex: 5,
                    lineHeight: 1,
                  }}
                >
                  {label}
                </div>
              );
            }
            const totalPaid = (previewInvoice.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
            const total = Number(previewInvoice.total || 0);
            const balance = Math.max(0, total - totalPaid);
            const isFullyPaid = previewInvoice.status === 'paid' || (total > 0 && balance <= 0.005);
            const isPartial = !isFullyPaid && totalPaid > 0;
            const dueDate = previewInvoice.dueDate ? new Date(previewInvoice.dueDate) : null;
            const isOverdue = !isFullyPaid && dueDate && dueDate < new Date();

            // PAID → use the branded stamp PNG the user uploaded. Everything
            // else falls back to the light-tone text stamp.
            if (isFullyPaid) {
              return (
                <img
                  src="/paid-stamp.png"
                  alt="PAID"
                  data-testid="invoice-preview-stamp-paid"
                  style={{
                    position: 'absolute',
                    top: '55%',
                    left: '58%',
                    transform: 'translate(-50%, -50%)',
                    width: '280px',
                    height: 'auto',
                    pointerEvents: 'none',
                    zIndex: 5,
                    // let it visually mix with the invoice paper so it doesn't
                    // look like a sticker slapped on top
                    mixBlendMode: 'multiply',
                  }}
                />
              );
            }

            let label = null;
            let light = null;
            if (isOverdue)                                { label = 'OVERDUE'; light = 'rgb(252, 165, 165)'; }
            else if (isPartial)                           { label = 'PARTIAL'; light = 'rgb(253, 186, 116)'; }
            else if (previewInvoice.status === 'draft')   { label = 'DRAFT';   light = 'rgb(203, 213, 225)'; }
            else                                          { label = 'PENDING'; light = 'rgb(252, 211, 77)';  }

            return (
              <div
                data-testid={`invoice-preview-stamp-${label.toLowerCase()}`}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '68%',
                  left: '62%',
                  transform: 'translate(-50%, -50%) rotate(-18deg)',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '4rem',
                  letterSpacing: '0.12em',
                  color: light,
                  border: `6px double ${light}`,
                  padding: '0.35rem 1.4rem',
                  borderRadius: 8,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  zIndex: 5,
                  lineHeight: 1,
                }}
              >
                {label}
              </div>
            );
          })()}

          <div style={{ padding: '2rem 2.25rem', position: 'relative', zIndex: 1 }}>
          {/* Invoice Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
              {companyInfo.logo ? (
                <img
                  src={companyInfo.logo}
                  alt="Company Logo"
                  data-testid="preview-company-logo"
                  style={{ width: '104px', height: '104px', objectFit: 'contain', flexShrink: 0 }}
                />
              ) : null}
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>{companyInfo.name}</div>
                {companyInfo.address && <div style={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'pre-line' }}>{companyInfo.address}</div>}
                {companyInfo.phone && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{companyInfo.phone}</div>}
                {companyInfo.email && <div style={{ fontSize: '0.8rem', color: '#249cdb' }}>{companyInfo.email}</div>}
              </div>
            </div>
            <div style={{ textAlign: 'right', minWidth: 220 }}>
              <div
                data-testid="preview-invoice-title"
                style={{
                  display: 'inline-block',
                  padding: '0.35rem 1.25rem',
                  border: '2px solid #249cdb',
                  borderRadius: '4px',
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.6rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  letterSpacing: '0.02em',
                  marginBottom: '0.75rem',
                }}
              >
                {docLabel}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.7 }}>
                <div><span style={{ color: '#94a3b8' }}>{docLabel} Number: </span><strong style={{ color: '#0f172a' }}>{docNumber}</strong></div>
                {previewInvoice.poNumber && (
                  <div data-testid="invoice-preview-po-number"><span style={{ color: '#94a3b8' }}>P.O./S.O. #: </span><strong style={{ color: '#0f172a' }}>{previewInvoice.poNumber}</strong></div>
                )}
                <div><span style={{ color: '#94a3b8' }}>{docLabel} Date: </span><strong style={{ color: '#0f172a' }}>{formatInvoiceDate(previewInvoice.date || previewInvoice.createdAt || previewInvoice.issueDate) || '—'}</strong></div>
                {previewInvoice.dueDate && (
                  <div><span style={{ color: '#94a3b8' }}>{docType === 'estimate' ? 'Valid Until: ' : 'Payment Due: '}</span><strong style={{ color: '#0f172a' }}>{formatInvoiceDate(previewInvoice.dueDate)}</strong></div>
                )}
                {(() => {
                  const totalPaid = (previewInvoice.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
                  const balance = Math.max(0, Number(previewInvoice.total || 0) - totalPaid);
                  const label = docType === 'estimate' ? 'Total (USD): ' : 'Amount Due (USD): ';
                  const value = docType === 'estimate' ? Number(previewInvoice.total || 0) : balance;
                  return (
                    <div style={{ marginTop: 6, fontSize: '0.85rem' }}>
                      <span style={{ color: '#94a3b8' }}>{label}</span>
                      <strong style={{ color: '#0f172a', fontSize: '1.05rem' }}>${value.toFixed(2)}</strong>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
              Bill To
            </div>
            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>{previewInvoice.customerName}</div>
            {previewInvoice.customerContactName && <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>{previewInvoice.customerContactName}</div>}
            {previewInvoice.customerAddress && <div style={{ fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-line' }}>{previewInvoice.customerAddress}</div>}
            {previewInvoice.customerEmail && <div style={{ fontSize: '0.85rem', color: '#249cdb', textDecoration: 'underline' }}>{previewInvoice.customerEmail}</div>}
            {previewInvoice.customerPhone && <div style={{ fontSize: '0.85rem', color: '#475569' }}>{previewInvoice.customerPhone}</div>}
          </div>

          {/* Invoice Details */}
          <div style={{ marginBottom: '1.25rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                  <th style={{ padding: '0.6rem 0.5rem', textAlign: 'left', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Items</th>
                  <th style={{ padding: '0.6rem 0.5rem', textAlign: 'center', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Quantity</th>
                  <th style={{ padding: '0.6rem 0.5rem', textAlign: 'right', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Price</th>
                  <th style={{ padding: '0.6rem 0.5rem', textAlign: 'right', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(previewInvoice.items || []).map((item, index) => (
                  <tr key={item.id || `item-${index}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#0f172a' }}>{item.description}</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', color: '#475569' }}>{item.quantity}</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: '#475569' }}>${Number(item.rate || 0).toFixed(2)}</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>${Number(item.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ marginLeft: 'auto', width: '320px', marginBottom: '2rem', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', color: '#475569' }}>
              <span>Subtotal:</span>
              <span style={{ color: '#0f172a', fontWeight: 500 }}>${Number(previewInvoice.subtotal || 0).toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', color: '#475569' }}>
              <span>Sales Tax ({Number(previewInvoice.taxRate || 0).toFixed(previewInvoice.taxRate && previewInvoice.taxRate % 1 !== 0 ? 3 : 0)}%):</span>
              <span style={{ color: '#0f172a', fontWeight: 500 }}>${Number(previewInvoice.tax || 0).toFixed(2)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.6rem 0',
                borderTop: '2px solid #cbd5e1',
                marginTop: '0.25rem',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#0f172a',
              }}
            >
              <span>Total:</span>
              <span>${Number(previewInvoice.total || 0).toFixed(2)}</span>
            </div>
            {(() => {
              const totalPaid = (previewInvoice.payments || []).reduce(
                (sum, p) => sum + Number(p.amount || 0),
                0,
              );
              const balance = Number(previewInvoice.total || 0) - totalPaid;
              if (totalPaid <= 0) {
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.6rem 0.75rem',
                      marginTop: '0.5rem',
                      background: '#f1f5f9',
                      borderTop: '2px solid #249cdb',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#0f172a',
                    }}
                  >
                    <span>{docType === 'estimate' ? 'Total (USD):' : 'Amount Due (USD):'}</span>
                    <span>${Math.max(0, balance).toFixed(2)}</span>
                  </div>
                );
              }
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', color: '#059669' }}>
                    <span>Amount Paid:</span>
                    <span>-${totalPaid.toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.6rem 0.75rem',
                      marginTop: '0.5rem',
                      background: balance > 0.005 ? '#fef2f2' : '#ecfdf5',
                      borderTop: `2px solid ${balance > 0.005 ? '#dc2626' : '#059669'}`,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: balance > 0.005 ? '#991b1b' : '#065f46',
                    }}
                  >
                    <span>{docType === 'estimate' ? 'Total (USD):' : (balance > 0.005 ? 'Amount Due (USD):' : 'Paid in Full')}</span>
                    <span>${Math.max(0, balance).toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Payment history */}
          {(previewInvoice.payments || []).length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', color: '#374151' }}>
                Payment History
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {previewInvoice.payments.map((p, i) => (
                  <div
                    key={p.id || `pay-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.6rem 0.85rem',
                      borderBottom:
                        i < previewInvoice.payments.length - 1 ? '1px solid #f3f4f6' : 'none',
                      fontSize: '0.85rem',
                      background: i % 2 === 0 ? '#fff' : '#fafafa',
                    }}
                  >
                    <div style={{ color: '#6b7280' }}>
                      {p.date ? new Date(p.date).toLocaleDateString() : '—'}
                      {p.method ? ` • ${p.method}` : ''}
                      {p.note ? ` • ${p.note}` : ''}
                    </div>
                    <div style={{ fontWeight: 600, color: '#059669' }}>
                      ${Number(p.amount || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes & Terms */}
          {previewInvoice.notes && (
            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Notes:</div>
              <div style={{ color: '#6b7280' }}>{previewInvoice.notes}</div>
            </div>
          )}

          {previewInvoice.terms && (
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '6px', marginBottom: '2rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Terms:</div>
              <div style={{ color: '#6b7280' }}>{previewInvoice.terms}</div>
            </div>
          )}

          {/* Footer thank-you note (per-invoice → company preset → default) */}
          <div
            style={{
              marginTop: '2rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center',
              fontStyle: 'italic',
              color: '#64748b',
              fontSize: '0.9rem',
            }}
            data-testid="invoice-preview-footer-note"
          >
            {previewInvoice.footerNote || 'Thank you for supporting my small business!'}
          </div>

          {/* Signature Section */}
          {companySignature && (
            <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '2px solid #e5e7eb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                <div>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Authorized Signature:
                  </div>
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '0.5rem',
                      background: '#f9fafb',
                      minHeight: '80px',
                      display: 'flex',
                      alignItems: 'flex-end',
                    }}
                  >
                    <img
                      src={companySignature}
                      alt="Authorized Signature"
                      style={{ maxWidth: '200px', maxHeight: '60px', width: 'auto', height: 'auto' }}
                    />
                  </div>
                  <div
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      borderTop: '1px solid #d1d5db',
                      paddingTop: '0.25rem',
                      fontWeight: 500,
                    }}
                  >
                    {companyInfo.name}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>Customer Signature:</span>
                    {!previewInvoice.customerSignature && (
                      <button
                        onClick={onRequestCustomerSignature}
                        className="no-print"
                        data-testid="request-customer-signature"
                        style={{
                          padding: '0.375rem 0.75rem',
                          background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          fontSize: '0.75rem',
                        }}
                      >
                        <Pen size={14} />
                        Sign Invoice
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '0.5rem',
                      background: previewInvoice.customerSignature ? '#f9fafb' : '#fff',
                      minHeight: '80px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                    }}
                  >
                    {previewInvoice.customerSignature ? (
                      <img
                        src={previewInvoice.customerSignature}
                        alt="Customer Signature"
                        style={{ maxWidth: '200px', maxHeight: '60px', width: 'auto', height: 'auto' }}
                      />
                    ) : (
                      <span style={{ color: '#d1d5db', fontSize: '0.875rem', fontStyle: 'italic' }}>
                        Not signed
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      borderTop: '1px solid #d1d5db',
                      paddingTop: '0.25rem',
                    }}
                  >
                    {previewInvoice.signatureDate ? (
                      <span>Date: {formatInvoiceDate(previewInvoice.signatureDate)}</span>
                    ) : (
                      <span>Date: _______________</span>
                    )}
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: '1.5rem',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  textAlign: 'center',
                  fontStyle: 'italic',
                }}
              >
                This invoice represents an agreement between {companyInfo.name} and {previewInvoice.customerName}
              </div>
            </div>
          )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewModal;
